import React, { useContext, useMemo, useState } from "react";
import { DatePicker, Empty, Spin, Table, message } from "antd";
import moment from "moment";
import { DeviceContext } from "../../components/device-provider/device-provider";
import { fetchOperatorDocuments, fetchOperatorBilling } from "../../actions/settlement.action";
import { getRangeOptions } from "../reports/reports.module";
import "./settlements-billing.page.scss";

const { RangePicker } = DatePicker;
const ISO_DATE = "YYYY-MM-DD";
const RANGE_PRESETS = getRangeOptions(60);

// ---------- formatting ----------
const inr = (n) => {
    const num = Number(n || 0);
    const s = String(Math.abs(Math.round(num * 100) / 100));
    const [intPart, dec] = s.split(".");
    const last3 = intPart.slice(-3);
    const rest = intPart.slice(0, -3);
    const grouped = rest ? rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + last3 : last3;
    return (num < 0 ? "-₹" : "₹") + grouped + (dec ? "." + dec : "");
};
const fmtDate = (d) => (d ? moment(d).format("DD MMM YYYY") : "—");
const fmtDateTime = (d) => (d ? moment(d).format("DD MMM YYYY, HH:mm") : "—");
const titleCase = (s) => String(s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const periodText = (p) => {
    if (!p) return "—";
    if (typeof p === "string") return p;
    if (p.from || p.to) return `${fmtDate(p.from)} → ${fmtDate(p.to)}`;
    return "—";
};

// status token -> [dot, bg, fg, label]
const OK = "#22a35b", BLUE = "#479FF8", YELLOW = "#EFBD40";
const STATUS = {
    paid: [OK, "rgba(34,163,91,0.12)", "#177544", "Paid"],
    approved: [BLUE, "rgba(71,159,248,0.15)", "#1a5fa8", "Payout pending"],
    issued: [BLUE, "rgba(71,159,248,0.15)", "#1a5fa8", "Unpaid"],
    part_paid: [YELLOW, "rgba(239,189,64,0.18)", "#8a6410", "Part paid"],
    signed: [OK, "rgba(34,163,91,0.12)", "#177544", "Signed"],
    verified: [OK, "rgba(34,163,91,0.12)", "#177544", "Verified"]
};
const StatusPill = ({ status, label }) => {
    const [dot, bg, fg, defLabel] = STATUS[status] || [BLUE, "var(--bg-sunk)", "var(--ink-2)", titleCase(status)];
    return (
        <span className="qb-status" style={{ background: bg, color: fg }}>
            <span className="dot" style={{ background: dot }} />{label || defLabel}
        </span>
    );
};

const KIND_LABELS = {
    settlement_note: "Settlement Note", proforma: "Pro-forma",
    gst_invoice: "GST Invoice", agreement: "Agreement", credit_note: "Credit Note"
};
const INVOICE_KINDS = ["proforma", "gst_invoice", "credit_note"];

const DownloadIcon = () => (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
        <path d="M8 2v9m0 0 3-3m-3 3L5 8M3 13h10" />
    </svg>
);
const Pdf = ({ url, expiresAt }) => {
    if (!url) return <span style={{ color: "var(--ink-4)" }}>—</span>;
    const expired = expiresAt && moment(expiresAt).isBefore(moment());
    return (
        <a className="dl" href={expired ? undefined : url} target="_blank" rel="noopener noreferrer"
           style={expired ? { color: "var(--ink-4)", cursor: "not-allowed" } : {}}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><DownloadIcon /> PDF</span>
        </a>
    );
};

const TABS = [
    { key: "settlements", label: "Settlements" },
    { key: "invoices", label: "Invoices" },
    { key: "payments", label: "Payments" }
];

const num = (v, style) => <span className="qb-num" style={style}>{v}</span>;
const money = (v, style, dash) => num(v == null ? (dash || "—") : inr(v), style);

// Expanded-row content (desktop antd Table) — the same breakdown the mobile
// card shows on tap.
const renderDetail = (r) => (
    <div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px 40px" }}>
            {(r.lines || []).map((l, i) => (
                <div key={i} style={{ minWidth: 160 }}>
                    <div className="kv-label">{l[0]}</div>
                    <div className="kv-val qb-num">{typeof l[1] === "number" ? inr(l[1]) : l[1]}</div>
                </div>
            ))}
        </div>
        {r.pdfUrl && (
            <div style={{ marginTop: 14 }}>
                <a className="qb-btn" href={r.pdfUrl} target="_blank" rel="noopener noreferrer"><DownloadIcon /> Download PDF</a>
            </div>
        )}
    </div>
);

const DESKTOP_COLUMNS = {
    settlements: [
        { title: "Note", dataIndex: "number", render: (v) => num(v, { fontWeight: 500, color: "var(--brand-2)" }) },
        { title: "Cycle / period", key: "period", render: (_, r) => num(periodText(r.period)) },
        { title: "Settlement date", dataIndex: "date", render: (v) => num(fmtDate(v)) },
        { title: "Credit", dataIndex: "credit", align: "right", render: (v) => money(v) },
        { title: "Withheld", dataIndex: "withheld", align: "right", render: (v) => money(v, { color: "var(--ink-3)" }) },
        { title: "Payout", dataIndex: "payout", align: "right", render: (v) => money(v, { fontWeight: 600, color: "#177544" }) },
        { title: "Carried", dataIndex: "carried", align: "right", render: (v) => money(v, { color: Number(v) > 0 ? "var(--warn)" : "var(--ink-4)" }) },
        { title: "Status", dataIndex: "status", render: (v, r) => <StatusPill status={v} label={r.displayStatus} /> },
        { title: "PDF", key: "pdf", render: (_, r) => <Pdf url={r.pdfUrl} expiresAt={r.pdfExpiresAt} /> }
    ],
    invoices: [
        {
            title: "Invoice no.", dataIndex: "number",
            render: (v, r) => (
                <span className="qb-num" style={{ fontWeight: 500 }}>
                    {v}{r.outsideRange ? <span style={{ marginLeft: 6, fontSize: 10, color: "#a81d1d", fontFamily: "var(--font-ui)" }}>older · unpaid</span> : null}
                </span>
            )
        },
        { title: "Type", dataIndex: "kind", render: (v) => <span className="qb-status" style={{ background: "var(--bg-sunk)", color: "var(--ink-2)" }}>{KIND_LABELS[v] || titleCase(v)}</span> },
        { title: "Period covered", key: "period", render: (_, r) => num(periodText(r.period)) },
        { title: "Due date", dataIndex: "dueDate", render: (v) => num(fmtDate(v)) },
        { title: "Amount", dataIndex: "total", align: "right", render: (v) => money(v) },
        {
            title: "Balance due", dataIndex: "balance", align: "right",
            render: (v, r) => {
                const overdue = r.status === "part_paid" || (Number(v) > 0 && r.outsideRange);
                return money(v, { fontWeight: 500, color: v ? (overdue ? "var(--warn)" : "var(--ink-1)") : "var(--ink-4)" });
            }
        },
        { title: "Status", dataIndex: "status", render: (v, r) => <StatusPill status={v} label={r.displayStatus} /> },
        { title: "PDF", key: "pdf", render: (_, r) => <Pdf url={r.pdfUrl} expiresAt={r.pdfExpiresAt} /> }
    ],
    payments: [
        { title: "S.No", dataIndex: "sno", align: "right", width: 70, render: (v) => num(v, { color: "var(--ink-4)" }) },
        { title: "Date", dataIndex: "date", render: (v) => num(fmtDate(v)) },
        { title: "Invoice(s)", key: "inv", render: (_, r) => <span className="qb-num">{r.invoiceList}{r.unallocated ? <div style={{ fontSize: 11.5, color: "var(--ink-4)" }}>+ {inr(r.unallocated)} unallocated</div> : null}</span> },
        { title: "Mode", dataIndex: "mode", render: (v) => <span className="qb-status" style={{ background: "var(--bg-sunk)", color: "var(--ink-2)" }}>{v}</span> },
        { title: "Amount", dataIndex: "amount", align: "right", render: (v) => money(v, { fontWeight: 600 }) },
        { title: "Status", dataIndex: "status", render: (v, r) => <StatusPill status={v} label={r.displayStatus} /> }
    ]
};
const SCROLL_X = { settlements: 1080, invoices: 980, payments: 780 };

export const SettlementsBillingPage = () => {
    const { isMobile } = useContext(DeviceContext);

    const [loading, setLoading] = useState(false);
    const [docs, setDocs] = useState(null);        // documents payload
    const [billing, setBilling] = useState(null);  // billing payload
    const [dateRange, setDateRange] = useState([
        moment().subtract(12, "months").startOf("day"),
        moment().endOf("day")
    ]);
    const [tab, setTab] = useState("settlements");
    const [openRow, setOpenRow] = useState(null);
    const [search, setSearch] = useState("");

    const loaded = !!docs || !!billing;
    const operator = (docs && docs.operator) || (billing && billing.operator) || null;

    // ---------- fetch documents + billing together ----------
    const runFetch = (range) => {
        const r = range || dateRange;
        const from = r && r[0];
        const to = r && r[1];
        if (!from || !to) { message.error("Please select a date range"); return; }
        if (from.isAfter(to, "day")) { message.error("From date cannot be after To date"); return; }

        setLoading(true);
        setOpenRow(null);
        let pending = 2;
        let hardError = null;
        const done = () => {
            if (--pending === 0) {
                setLoading(false);
                if (hardError) message.error(hardError);
            }
        };
        fetchOperatorDocuments(
            { operatorCode: operator && operator.code, from: from.format(ISO_DATE), to: to.format(ISO_DATE), kinds: [] },
            (resp) => { setDocs(resp || { documents: [] }); done(); },
            (err) => { hardError = err || "Failed to fetch documents"; done(); }
        );
        // billing is best-effort — its failure shouldn't block the documents view.
        fetchOperatorBilling(
            { operatorCode: operator && operator.code },
            (resp) => { setBilling(resp || null); done(); },
            () => { done(); }
        );
    };

    // ---------- derived rows ----------
    const documents = (docs && docs.documents) || [];

    const settlementRows = useMemo(() => {
        return documents.filter((d) => d.kind === "settlement_note").map((d) => {
            const s = d.settlement || {};
            const cycle = s.mode ? titleCase(s.mode) + (s.cycleDay ? ` · ${titleCase(s.cycleDay)}` : "") : null;
            return {
                key: `stl-${d.id}`, number: d.number, period: d.period, date: d.date,
                credit: s.creditAvailable, withheld: s.withheldTotal, payout: d.amount, carried: s.carriedForward,
                status: d.status, displayStatus: d.displayStatus,
                paidOn: s.paidOn, utr: s.utr, pdfUrl: d.pdfUrl, pdfExpiresAt: d.pdfExpiresAt,
                lines: [
                    ["Cycle", cycle || "—"],
                    ["Credit available", s.creditAvailable],
                    ["Withheld", s.withheldTotal],
                    ["Payout", d.amount],
                    ["Carried forward", s.carriedForward],
                    ["Paid on", s.paidOn ? fmtDate(s.paidOn) : "—"],
                    ["UTR / reference", s.utr || "—"]
                ].filter(Boolean)
            };
        });
    }, [documents]);

    const invoiceRows = useMemo(() => {
        const openById = {};
        ((billing && billing.openInvoices) || []).forEach((o) => { openById[o.id] = o; });
        return documents.filter((d) => INVOICE_KINDS.indexOf(d.kind) !== -1).map((d) => {
            const iv = d.invoice || {};
            const open = openById[d.id];
            const balance = d.balance != null ? d.balance : (open ? open.balance : null);
            return {
                key: `inv-${d.id}`, number: d.number, kind: d.kind, period: d.period,
                date: d.date, total: d.amount, balance, paid: iv.paid, dueDate: iv.dueDate,
                status: d.status, displayStatus: d.displayStatus, outsideRange: d.outsideRange,
                pdfUrl: d.pdfUrl, pdfExpiresAt: d.pdfExpiresAt,
                lines: [
                    ["Type", KIND_LABELS[d.kind] || titleCase(d.kind)],
                    ["Amount incl. GST", d.amount],
                    iv.paid != null ? ["Paid", iv.paid] : null,
                    ["Balance due", balance == null ? "—" : inr(balance)],
                    iv.dueDate ? ["Due date", fmtDate(iv.dueDate)] : null
                ].filter(Boolean)
            };
        });
    }, [documents, billing]);

    const paymentRows = useMemo(() => {
        const src = (docs && docs.payments) || (billing && billing.recentPayments) || [];
        return src.map((p, i) => ({
            key: `pay-${p.id}`, sno: i + 1, date: p.date, amount: p.amount,
            mode: p.modeLabel || titleCase(p.mode), reference: p.reference, unallocated: p.unallocated,
            status: p.verified ? "verified" : (p.status || undefined), displayStatus: p.displayStatus,
            invoiceList: (p.allocations || []).map((a) => a.number).join(", ") || p.reference || "—",
            lines: [
                ["Mode", p.modeLabel || titleCase(p.mode)],
                ["Reference / UTR", p.reference || "—"],
                ["Applied to", (p.allocations || []).map((a) => `${a.number} (${inr(a.amount)})`).join(", ") || "—"],
                p.unallocated ? ["Unallocated", inr(p.unallocated)] : null
            ].filter(Boolean)
        }));
    }, [docs, billing]);

    const counts = { settlements: settlementRows.length, invoices: invoiceRows.length, payments: paymentRows.length };
    const activeRows = tab === "settlements" ? settlementRows : tab === "invoices" ? invoiceRows : paymentRows;

    const filteredRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return activeRows;
        return activeRows.filter((r) =>
            [r.number, r.reference, r.invoiceList, r.utr].filter(Boolean).some((v) => String(v).toLowerCase().includes(q))
        );
    }, [activeRows, search]);

    // ---------- summary ----------
    const summary = useMemo(() => {
        const b = billing || {};
        const collectionShown = !b.collection || b.collection.shown !== false;
        const settledPayout = settlementRows
            .filter((s) => s.status === "paid")
            .reduce((a, s) => a + (Number(s.payout) || 0), 0);
        return {
            creditBalance: b.creditBalance,
            balanceDue: collectionShown ? b.balanceDue : null,
            awaitingVerification: collectionShown ? b.awaitingVerification : null,
            collectionShown,
            reason: b.collection && b.collection.reason,
            settledPayout
        };
    }, [billing, settlementRows]);

    // ---------- gate ----------
    const gate = (
        <div className="qb-card" style={{ maxWidth: 720 }}>
            <div className="qb-eyebrow">Step 1 of 1</div>
            <h2 style={{ fontSize: 17, fontWeight: 600, margin: "6px 0" }}>Pick a date range to view your statement</h2>
            <p style={{ fontSize: 13, color: "var(--ink-3)", marginBottom: 16 }}>
                Settlements, invoices and payments are reported against the range you choose. Nothing is loaded until then.
            </p>
            <div className="qb-eyebrow" style={{ marginBottom: 8 }}>Date range</div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                <RangePicker
                    value={dateRange}
                    onChange={setDateRange}
                    format="DD MMM YYYY"
                    allowClear={false}
                    ranges={RANGE_PRESETS}
                    style={{ width: isMobile ? "100%" : 280 }}
                />
                <button className="qb-btn primary" style={{ height: 34, padding: "0 16px" }} onClick={() => runFetch()}>
                    View statement
                </button>
            </div>
        </div>
    );

    // ---------- summary cards ----------
    const summaryCards = (
        <div className="qb-summary" style={isMobile ? undefined : { maxWidth: 1400, marginLeft: "auto", marginRight: "auto" }}>
            {/* <div className="qb-card">
                <div className="qb-eyebrow">Credit balance</div>
                <div className="qb-metric">{summary.creditBalance == null ? "—" : inr(summary.creditBalance)}</div>
                <div className="qb-metric-sub">Held for you, net of settlements</div>
            </div>
            <div className="qb-card">
                <div className="qb-eyebrow">Settled payout</div>
                <div className="qb-metric">{inr(summary.settledPayout)}</div>
                <div className="qb-metric-sub">{counts.settlements} settlement(s)</div>
            </div>
            <div className="qb-card brand">
                <div className="qb-eyebrow" style={{ color: "var(--brand)" }}>Balance due</div>
                <div className="qb-metric hero">{summary.balanceDue == null ? "—" : inr(summary.balanceDue)}</div>
                <div className="qb-metric-sub">
                    {summary.collectionShown ? `${counts.invoices} invoice(s)` : (summary.reason || "Withheld from settlements")}
                </div>
            </div>
            <div className="qb-card">
                <div className="qb-eyebrow">Awaiting verification</div>
                <div className="qb-metric">{summary.awaitingVerification == null ? "—" : inr(summary.awaitingVerification)}</div>
                <div className="qb-metric-sub">Reported, not yet confirmed</div>
            </div> */}
        </div>
    );

    // ---------- mobile row card ----------
    const renderMobileRow = (r) => {
        const open = openRow === r.key;
        const toggle = () => setOpenRow(open ? null : r.key);
        const primary = tab === "settlements" ? r.payout : tab === "invoices" ? r.total : r.amount;
        const primaryLabel = tab === "settlements" ? "Payout" : "Amount";
        let secondary, secondaryLabel;
        if (tab === "invoices") {
            secondaryLabel = "Balance due";
            secondary = r.balance == null ? "—" : (Number(r.balance) > 0 ? inr(r.balance) : "Paid");
        } else if (tab === "payments") {
            secondaryLabel = "Date";
            secondary = fmtDate(r.date);
        } else {
            secondaryLabel = "Period";
            secondary = periodText(r.period);
        }
        const status = tab === "payments" ? (r.status || "verified") : r.status;
        const idText = tab === "payments" ? r.invoiceList : r.number;
        const subText = tab === "settlements" ? (r.paidOn ? `Paid ${fmtDate(r.paidOn)}` : fmtDate(r.date)) : tab === "invoices" ? (KIND_LABELS[r.kind] || titleCase(r.kind)) : r.mode;
        return (
            <div className="qb-card qb-mcard" key={r.key} onClick={toggle}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
                    <div>
                        <div className="qb-num" style={{ fontWeight: 500 }}>{idText}</div>
                        <div className="qb-num" style={{ fontSize: 11, color: "var(--ink-4)", marginTop: 2 }}>{subText}</div>
                    </div>
                    <StatusPill status={status} label={r.displayStatus} />
                </div>
                <div className="qb-mhero">
                    <div>
                        <div className="qb-eyebrow">{primaryLabel}</div>
                        <div className="qb-num" style={{ fontSize: 17, fontWeight: 500, color: tab === "settlements" ? "#177544" : "var(--ink-1)" }}>{inr(primary)}</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                        <div className="qb-eyebrow">{secondaryLabel}</div>
                        <div className="qb-num" style={{ fontSize: 12.5, color: "var(--ink-2)", marginTop: 2 }}>{secondary}</div>
                    </div>
                </div>
                {open && (
                    <div style={{ marginTop: 12, paddingTop: 11, borderTop: "1px dashed var(--line-strong)", display: "flex", flexDirection: "column", gap: 7 }}>
                        {r.lines.map((l, i) => (
                            <div className="line-row" key={i}>
                                <span style={{ color: "var(--ink-3)" }}>{l[0]}</span>
                                <span className="qb-num" style={{ color: "var(--ink-1)" }}>{typeof l[1] === "number" ? inr(l[1]) : l[1]}</span>
                            </div>
                        ))}
                        {r.pdfUrl && (
                            <a className="qb-btn" style={{ justifyContent: "center", marginTop: 4 }} href={r.pdfUrl} target="_blank" rel="noopener noreferrer"><DownloadIcon /> Download PDF</a>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // ---------- settlement totals (sum row) ----------
    const sumSettlements = (rows) => rows.reduce((a, r) => ({
        credit: a.credit + (Number(r.credit) || 0),
        withheld: a.withheld + (Number(r.withheld) || 0),
        payout: a.payout + (Number(r.payout) || 0),
        carried: a.carried + (Number(r.carried) || 0)
    }), { credit: 0, withheld: 0, payout: 0, carried: 0 });

    // antd Table.Summary footer for the Settlements tab. Cell index 0 is the
    // Table's expand column; the 9 data columns follow at indices 1..9.
    const settlementSummary = (pageData) => {
        const t = sumSettlements(pageData);
        const cell = (idx, node, align) => <Table.Summary.Cell index={idx} align={align}>{node}</Table.Summary.Cell>;
        return (
            <Table.Summary fixed>
                <Table.Summary.Row style={{ background: "var(--surface-2)" }}>
                    {cell(0, null)}
                    {cell(1, <span style={{ fontWeight: 600 }}>Total</span>)}
                    {cell(2, num(`${pageData.length} settlement(s)`, { color: "var(--ink-3)" }))}
                    {cell(3, null)}
                    {cell(4, num(inr(t.credit), { fontWeight: 600 }), "right")}
                    {cell(5, num(inr(t.withheld), { fontWeight: 600, color: "var(--ink-3)" }), "right")}
                    {cell(6, num(inr(t.payout), { fontWeight: 700, color: "#177544" }), "right")}
                    {cell(7, num(inr(t.carried), { fontWeight: 600, color: Number(t.carried) > 0 ? "var(--warn)" : "var(--ink-4)" }), "right")}
                    {cell(8, null)}
                    {cell(9, null)}
                </Table.Summary.Row>
            </Table.Summary>
        );
    };

    const renderMobileSettlementTotals = (rows) => {
        const t = sumSettlements(rows);
        const col = (label, val, color) => (
            <div style={{ flex: 1 }}>
                <div className="qb-eyebrow">{label}</div>
                <div className="qb-num" style={{ fontSize: 13, marginTop: 2, color: color || "var(--ink-1)" }}>{inr(val)}</div>
            </div>
        );
        return (
            <div className="qb-card qb-mcard" style={{ cursor: "default", background: "var(--surface-2)" }}>
                <div style={{ fontWeight: 600, fontSize: 12.5 }}>Total · <span className="qb-num">{rows.length}</span> settlement(s)</div>
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                    {col("Credit", t.credit)}
                    {col("Withheld", t.withheld, "var(--ink-3)")}
                    {col("Payout", t.payout, "#177544")}
                    {col("Carried", t.carried, Number(t.carried) > 0 ? "var(--warn)" : "var(--ink-4)")}
                </div>
            </div>
        );
    };

    // ---------- tab bar ----------
    const tabBar = (
        <div className="qb-tabs">
            {TABS.map((t) => (
                <div key={t.key} className={"qb-tab" + (tab === t.key ? " active" : "")} onClick={() => { setTab(t.key); setOpenRow(null); }}>
                    <span>{t.label}</span><span className="count">{counts[t.key]}</span>
                </div>
            ))}
        </div>
    );

    return (
        <div className={"qb-billing" + (isMobile ? " mobile" : "")}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
                <div>
                    <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>Billing &amp; Settlements</h1>
                    <div style={{ fontSize: 12.5, color: "var(--ink-3)", marginTop: 2 }}>
                        Website collections, software charges and payments
                        {operator ? <> for <strong>{operator.legalName || operator.name || operator.code}</strong></> : null}
                    </div>
                </div>
                {loaded && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <RangePicker
                            value={dateRange}
                            onChange={(v) => { setDateRange(v); }}
                            format="DD MMM YYYY"
                            allowClear={false}
                            ranges={RANGE_PRESETS}
                            style={{ width: isMobile ? 220 : 260 }}
                        />
                        <button className="qb-btn primary" onClick={() => runFetch()}>Refresh</button>
                    </div>
                )}
            </div>

            {!loaded ? (
                <Spin spinning={loading}>{gate}</Spin>
            ) : (
                <Spin spinning={loading}>
                    {summaryCards}

                    <div className="qb-card" style={{ padding: 0, overflow: "hidden", ...(isMobile ? {} : { maxWidth: 1400, marginLeft: "auto", marginRight: "auto" }) }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "12px 16px", borderBottom: "1px solid var(--line)", flexWrap: "wrap" }}>
                            {tabBar}
                            <div className="qb-search">
                                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><circle cx="7" cy="7" r="4.5" /><path d="m11 11 3 3" /></svg>
                                <input placeholder="Search no., UTR, invoice…" value={search} onChange={(e) => setSearch(e.target.value)} />
                            </div>
                        </div>

                        {isMobile ? (
                            filteredRows.length === 0 ? (
                                <div style={{ padding: 32 }}><Empty description={`No ${tab} in this range`} /></div>
                            ) : (
                                <div style={{ padding: 12 }}>
                                    {filteredRows.map(renderMobileRow)}
                                    {tab === "settlements" && renderMobileSettlementTotals(filteredRows)}
                                </div>
                            )
                        ) : (
                            <Table
                                className="qb-billing-table"
                                rowKey="key"
                                size="middle"
                                columns={DESKTOP_COLUMNS[tab]}
                                dataSource={filteredRows}
                                pagination={false}
                                scroll={{ x: SCROLL_X[tab] }}
                                expandable={{ expandedRowRender: renderDetail, columnWidth: 40 }}
                                summary={tab === "settlements" ? settlementSummary : undefined}
                                locale={{ emptyText: <Empty description={`No ${tab} in this range`} /> }}
                            />
                        )}
                    </div>
                </Spin>
            )}
        </div>
    );
};
